import React, { useState, useEffect, useCallback, useRef } from 'react';
import { IconWallet, IconClock, IconAlert, IconInfo, IconCalendar } from '../common/Icons';
import { useI18n } from '../../services/i18n';
import {
  days,
  localDate,
  parseLocal,
  pad,
  money,
  fmtTime,
  MAX_HOURS,
  PRICE_PER_HOUR
} from '../../services/api';

export default function OrderForm({
  me,
  slots,
  loadSlots,
  model,
  setModel,
  dayIdx,
  setDayIdx,
  start,
  setStart,
  dur,
  setDur,
  onNavigate,
  onCreateOrder,
  busy,
  onOpenOnboarding
}) {
  const { t } = useI18n();
  const [now] = useState(() => new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const daysRef = useRef(null);

  const ds = React.useMemo(() => days(30), []);
  const selectedDate = ds[dayIdx] || ds[0];
  const currentDate = localDate(selectedDate);
  const [calDate, setCalDate] = useState(() => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));

  useEffect(() => {
    const cur = ds[dayIdx];
    if (cur) {
      setCalDate(new Date(cur.getFullYear(), cur.getMonth(), 1));
    }
  }, [dayIdx, ds]);

  const minMonthDate = new Date(ds[0].getFullYear(), ds[0].getMonth(), 1);
  const maxMonthDate = new Date(ds[ds.length - 1].getFullYear(), ds[ds.length - 1].getMonth(), 1);
  const isPrevMonthDisabled = calDate.getTime() <= minMonthDate.getTime();
  const isNextMonthDisabled = calDate.getTime() >= maxMonthDate.getTime();

  const handleCalPrevMonth = () => {
    if (!isPrevMonthDisabled) {
      setCalDate(new Date(calDate.getFullYear(), calDate.getMonth() - 1, 1));
    }
  };

  const handleCalNextMonth = () => {
    if (!isNextMonthDisabled) {
      setCalDate(new Date(calDate.getFullYear(), calDate.getMonth() + 1, 1));
    }
  };

  const calYear = calDate.getFullYear();
  const calMonth = calDate.getMonth();
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const firstWeekdayOffset = (new Date(calYear, calMonth, 1).getDay() + 6) % 7;

  const getDayShortLabel = (d, i) => {
    if (i === 0) return t('seg_today');
    const dayIndex = d.getDay();
    return t(`wd_${dayIndex}`);
  };

  const slot = useCallback((h) => slots?.find((s) => s.hour === h), [slots]);

  const fits = useCallback((st, d) => {
    if (st == null || !slots) return false;
    for (let h = st; h < st + d; h++) {
      if (h > 23) return false;
      const s = slot(h);
      if (!s || s.status !== 'free') return false;
    }
    return true;
  }, [slots, slot]);

  const firstFit = useCallback((d) => {
    for (let h = 0; h < 24; h++) {
      if (fits(h, d)) return h;
    }
    return null;
  }, [fits]);

  useEffect(() => {
    loadSlots(currentDate);
  }, [currentDate, loadSlots]);

  useEffect(() => {
    if (slots && start == null) {
      const fit = firstFit(dur);
      if (fit !== null) setStart(fit);
    }
  }, [slots, start, dur, firstFit, setStart]);

  useEffect(() => {
    if (daysRef.current && daysRef.current.children[dayIdx]) {
      const target = daysRef.current.children[dayIdx];
      if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [dayIdx]);

  const handleModelChange = (e) => {
    const raw = e.target.value;
    const clean = raw
      .trim()
      .replace(/^https?:\/\/(www\.)?stripchat\.com\//i, '')
      .replace(/\/.*$/, '');
    setModel(clean);
  };

  const handleDaySelect = (idx) => {
    const validIdx = Math.max(0, Math.min(ds.length - 1, idx));
    setDayIdx(validIdx);
    setStart(null);
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('light');
  };

  const handleHourSelect = (h) => {
    setStart(h);
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('light');
  };

  const handleDurChange = (delta) => {
    const next = Math.min(MAX_HOURS, Math.max(1, dur + delta));
    setDur(next);
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('light');
  };

  const getOrderError = () => {
    if (!model.trim()) return t('err_empty_model');
    if (!slots) return null;
    if (start == null) return t('err_no_window');
    for (let h = start; h < start + dur; h++) {
      if (h > 23) return t('err_after_midnight');
      const st = slot(h)?.status;
      if (st === 'busy') return t('err_hour_busy', { time: pad(h) });
      if (st === 'past') return t('err_past_time');
    }
    if (me && me.balance < (dur * PRICE_PER_HOUR)) return 'insufficient'
    return null;
  };

  const err = getOrderError();
  const price = PRICE_PER_HOUR;
  const cost = dur * price;
  const afterBalance = me ? me.balance - cost : 0;
  const tzOff = -now.getTimezoneOffset() / 60;

  const handleOrderSubmit = () => {
    if (err || !slots || busy) return;
    const startIso = parseLocal(currentDate, start).toISOString();
    onCreateOrder({ model, start: startIso, hours: dur });
  };

  return (
    <>
      <main className="screen" data-testid="screen-order">
        <div className="head">
          <div className="col" style={{ gap: '2px' }}>
            <span className="head__sub">{t('order_head_sub')}</span>
            <h1>{t('order_title')}</h1>
          </div>
          <button
            className="pill"
            onClick={() => onNavigate('wallet')}
            data-testid="order-header-balance"
          >
            <span style={{ color: 'var(--accent-text)', display: 'flex' }}>
              <IconWallet />
            </span>
            {me ? money(me.balance) : '…'}
          </button>
        </div>

        {/* Model field */}
        <section className="col">
          <label className="label" htmlFor="model-input">{t('model_label')}</label>
          <div className="field">
            <span className="field__prefix">stripchat.com/</span>
            <input
              id="model-input"
              value={model}
              onChange={handleModelChange}
              placeholder={t('model_placeholder')}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck="false"
              data-testid="model-input"
            />
          </div>
          {me?.recent_models && me.recent_models.length > 0 && (
            <div className="chips">
              <span className="small" style={{ color: 'var(--faint)' }}>{t('recent_label')}</span>
              {me.recent_models.map((m) => (
                <button
                  key={m}
                  className={`chip ${m === model ? 'is-on' : ''}`}
                  onClick={() => setModel(m)}
                  data-testid={`chip-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Date selector with horizontal scroll, paging, and month calendar */}
        <section className="col">
          <div className="row date-section-head">
            <span className="label">{t('date_label')}</span>
            <button
              type="button"
              className={`cal-toggle-btn ${showCalendar ? 'is-active' : ''}`}
              onClick={() => setShowCalendar((v) => !v)}
              aria-label="Toggle calendar"
              data-testid="btn-toggle-calendar"
            >
              <IconCalendar />
              <span>{t('calendar_btn')}</span>
            </button>
          </div>

          {/* Expandable monthly calendar view */}
          {showCalendar && (
            <div className="calendar-card" data-testid="calendar-popup">
              <div className="calendar-head">
                <button
                  type="button"
                  className="calendar-nav-btn"
                  onClick={handleCalPrevMonth}
                  disabled={isPrevMonthDisabled}
                  aria-label="Previous month"
                  data-testid="btn-cal-prev-month"
                >
                  ‹
                </button>
                <span className="calendar-title" data-testid="calendar-month-title">
                  {t(`month_full_${calMonth}`)} {calYear}
                </span>
                <button
                  type="button"
                  className="calendar-nav-btn"
                  onClick={handleCalNextMonth}
                  disabled={isNextMonthDisabled}
                  aria-label="Next month"
                  data-testid="btn-cal-next-month"
                >
                  ›
                </button>
              </div>

              <div className="calendar-weekdays">
                {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((w) => (
                  <span key={w} className="calendar-weekday">
                    {t(`cal_wd_${w}`)}
                  </span>
                ))}
              </div>

              <div className="calendar-grid">
                {Array.from({ length: firstWeekdayOffset }).map((_, i) => (
                  <div key={`empty-${i}`} className="calendar-cell is-empty" />
                ))}

                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const dayNum = i + 1;
                  const matchingIdx = ds.findIndex(
                    (d) =>
                      d.getFullYear() === calYear &&
                      d.getMonth() === calMonth &&
                      d.getDate() === dayNum
                  );
                  const isValid = matchingIdx !== -1;
                  const isSelected = matchingIdx === dayIdx;
                  const isToday = matchingIdx === 0;

                  let cellCls = 'calendar-day';
                  if (!isValid) cellCls += ' is-disabled';
                  if (isToday) cellCls += ' is-today';
                  if (isSelected) cellCls += ' is-selected';

                  return (
                    <button
                      key={dayNum}
                      type="button"
                      className={cellCls}
                      disabled={!isValid}
                      onClick={() => {
                        if (isValid) {
                          handleDaySelect(matchingIdx);
                        }
                      }}
                      data-testid={`cal-day-${calYear}-${calMonth}-${dayNum}`}
                    >
                      <span>{dayNum}</span>
                      {isToday && !isSelected && <span className="today-dot" />}
                    </button>
                  );
                })}
              </div>

              <div className="calendar-footer">
                <span className="calendar-limit-badge">{t('limit_30_days')}</span>
              </div>
            </div>
          )}

          {/* Horizontal scrollable date strip */}
          {!showCalendar && (
            <div className="days-wrap">
              <div className="days" ref={daysRef} data-testid="days-scroll">
                {ds.map((d, i) => (
                  <button
                    key={i}
                    className={`day ${i === dayIdx ? 'is-on' : ''}`}
                    onClick={() => handleDaySelect(i)}
                    data-testid={`day-${i}`}
                  >
                    <small>{getDayShortLabel(d, i)}</small>
                    <b>{d.getDate()}</b>
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Start time slot grid */}
        <section className="col">
          <div className="row">
            <span className="label">{t('start_time_label')}</span>
            <span className="small muted" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <IconClock />
              UTC{tzOff >= 0 ? `+${tzOff}` : tzOff} · {t('now_label')} {fmtTime(now)}
            </span>
          </div>

          {!slots ? (
            <div className="skeleton" data-testid="slots-loading" />
          ) : (
            <div className="hours" data-testid="slots-grid">
              {slots.map(({ hour, status }) => {
                const inR = start != null && hour >= start && hour < start + dur;
                let cls = 'hour';
                if (status === 'past') cls += ' hour--past';
                else if (inR && status === 'busy') cls += ' hour--conflict';
                else if (inR) cls += ' hour--sel';
                else if (status === 'busy') cls += ' hour--busy';

                const disabled = status === 'past' || (status === 'busy' && !inR);

                return (
                  <button
                    key={hour}
                    className={cls}
                    disabled={disabled}
                    onClick={() => handleHourSelect(hour)}
                    aria-label={`${pad(hour)}, ${status === 'busy' ? t('legend_busy') : status === 'past' ? t('err_past_time') : t('legend_free')}`}
                    data-testid={`hour-${hour}`}
                  >
                    {String(hour).padStart(2, '0')}
                  </button>
                );
              })}
            </div>
          )}

          <div className="legend">
            <span>
              <i style={{ background: 'var(--surface-2)', border: '1px solid var(--border-2)' }} />
              {t('legend_free')}
            </span>
            <span>
              <i style={{ background: 'repeating-linear-gradient(135deg,var(--surface) 0 3px,var(--border-2) 3px 6px)' }} />
              {t('legend_busy')}
            </span>
            <span>
              <i style={{ background: 'var(--accent)' }} />
              {t('legend_selected')}
            </span>
          </div>
        </section>

        {/* Duration stepper */}
        <section className="card" style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: '12px 12px 12px 16px' }}>
          <div className="col" style={{ gap: '2px' }}>
            <b>{t('duration_label')}</b>
            <span className="small muted">
              {start != null ? `${pad(start)} – ${pad(start + dur)}` : '—'}
            </span>
          </div>
          <div className="stepper">
            <button
              onClick={() => handleDurChange(-1)}
              aria-label="−"
              disabled={dur <= 1}
              data-testid="dur-minus"
            >
              −
            </button>
            <b data-testid="dur-value">{dur} {t('hours_unit')}</b>
            <button
              onClick={() => handleDurChange(1)}
              aria-label="+"
              disabled={dur >= MAX_HOURS}
              data-testid="dur-plus"
            >
              +
            </button>
          </div>
        </section>

        {/* Message / error / info */}
        <div id="order-msg">
          {err === 'insufficient' ? (
            <div className="note note--err" data-testid="msg-insufficient">
              <IconAlert />
              <span>{t('err_insufficient_prefix')} {money(dur * price - (me?.balance || 0))} {t('err_insufficient_suffix')}</span>
            </div>
          ) : err ? (
            <div className="note note--err" data-testid="msg-error">
              <IconAlert />
              <span>{err}</span>
            </div>
          ) : start != null ? (
            <div className="note" data-testid="msg-info">
              <IconInfo />
              <span>
                {t('online_note_prefix')}{' '}
                <b style={{ color: 'var(--text)' }}>{String((start + 23) % 24).padStart(2, '0')}:45</b> {t('online_note_suffix')}{' '}
                <a
                  href="#how"
                  onClick={(e) => {
                    e.preventDefault();
                    onOpenOnboarding();
                  }}
                >
                  {t('how_it_works')}
                </a>
              </span>
            </div>
          ) : null}
        </div>
      </main>

      {/* Fixed bottom footer */}
      <div className="foot" data-testid="order-foot">
        <div className="foot__inner">
          <div className="row small" style={{ fontSize: '14px' }}>
            <span className="muted">{dur} {t('hours_unit')} × ${price}</span>
            <span className="muted">
              {t('remaining_label')} <b style={{ color: 'var(--text)' }}>{me ? money(afterBalance) : '…'}</b>
            </span>
          </div>

          {err === 'insufficient' ? (
            <button
              className="btn btn--primary"
              onClick={() => onNavigate('wallet')}
              data-testid="btn-topup-insufficient"
            >
              {t('btn_topup')}
            </button>
          ) : err || !slots ? (
            <button className="btn" disabled data-testid="btn-order-disabled">
              {slots ? t('btn_select_free_time') : t('btn_loading_slots')}
            </button>
          ) : (
            <button
              className="btn btn--primary"
              onClick={handleOrderSubmit}
              disabled={busy}
              data-testid="btn-create-order"
            >
              {busy ? t('btn_creating') : t('btn_order_for', { cost })}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
